import { type ReactNode } from 'react'
import clsx from 'clsx'

import type { MailVerdict } from '../../lib/data/types'
import { presetPhrase, PileSegs, withGhosts, type DayCtx } from './cards'
import type { DayItem, Section } from './controller'
import { pickedPhrase, senderShort } from './designed'
import { CheckGlyph, ChevronRight, MoonGlyph, XGlyph } from './icons'
import { SnoozeMenu } from './menus'
import { DayCard, Kc, RollingCount, SrcLine, Tip } from './parts'

// ============================================================================
// THE FOLDED PILES (Main board): Snoozed · Low priority · Noise — and, on a quiet day, Important.
//
// Each opens IN PLACE, at most 70% of the viewport tall, scrolling inside its own box with
// `overscroll-behavior: contain`; outside the box the page scrolls (owner, closed). The chevron
// turns 120 ms and the list is simply there (14h). A pile that just received a card washes violet
// once (14c). Every row takes the same keys as a full card, and its actions on hover.
// ============================================================================

export interface FoldPile {
  key: 'snoozed' | MailVerdict
  section: Section
  name: string
  count: number
  /** 'Amazon 4 · Stripe · 2 more' or 'next back tonight, 8:00 PM'. */
  summary: string | null
  items: DayItem[]
  /** The right-hand text of a row: its day, or when it comes back. */
  rowWhen: (item: DayItem) => string
  /** The quiet board draws the pile's key at the right. */
  keyHint?: string
}

export function FoldedCard({
  piles,
  open,
  onToggle,
  ctx,
  footer,
  label = 'Folded',
}: {
  piles: FoldPile[]
  open: string | null
  onToggle: (key: string) => void
  ctx: DayCtx
  footer: ReactNode[]
  label?: string
}) {
  return (
    <DayCard aria-label={label}>
      {piles.map((p, i) => {
        const isOpen = open === p.key
        return (
          <div key={p.key}>
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => onToggle(p.key)}
              data-day-nav=""
              data-day-fold={p.key}
              className={clsx(
                'day-fold day-tipw flex w-full cursor-pointer items-center gap-[10px] border-0 bg-transparent px-4 py-3 text-left font-[inherit] text-ink',
                i > 0 && 'border-t border-line-3',
                ctx.ctrl.flash === p.key && 'day-flash',
                i === 0 && 'rounded-t-card',
              )}
            >
              <span className="day-chev inline-flex text-muted" data-open={isOpen ? 'true' : undefined}>
                <ChevronRight size={14} />
              </span>
              {p.key === 'snoozed' && <MoonGlyph size={14} className="text-muted" />}
              <span className="text-[12.5px] font-semibold">{p.name}</span>
              <span className="text-[11.5px] font-medium text-muted">
                <RollingCount value={p.count} />
              </span>
              {p.summary && <span className="min-w-0 truncate text-[11.5px] text-microcopy">{p.summary}</span>}
              {p.keyHint && <Kc className="ml-auto">{p.keyHint}</Kc>}
              <Tip name={isOpen ? `Close ${p.name}` : `Open ${p.name}`} k="Enter">
                {p.key === 'snoozed'
                  ? 'Shows what is snoozed and when each comes back.'
                  : 'Opens in place; a long pile scrolls inside its own box.'}
              </Tip>
            </button>
            {isOpen && (
              <div className="day-plist border-t border-line-3 bg-surface-3" role="list" aria-label={p.name}>
                {withGhosts(p.items, ctx.ctrl.ghosts(p.section)).map(({ item, index }) => (
                  <PileRow key={item.key} item={item} section={p.section} index={index} ctx={ctx} when={p.rowWhen(item)} snoozed={p.key === 'snoozed'} />
                ))}
                {p.key !== 'snoozed' && (
                  <div className="sticky bottom-0 flex items-center gap-[5px] border-t border-line-3 bg-surface-3 py-2 pl-10 pr-4 text-[11px] text-microcopy">
                    <Kc>J</Kc>
                    <Kc>K</Kc> move · <Kc>1</Kc>–<Kc>4</Kc> re-sort · <Kc>Esc</Kc> close
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
      {footer.length > 0 && (
        <SrcLine
          parts={footer}
          className="rounded-b-card border-t border-line-3 bg-surface-3 px-4 py-[10px]"
        />
      )}
    </DayCard>
  )
}

/** One row of an opened pile: sender, subject, day — and on hover the card's own actions. */
function PileRow({ item, section, index, ctx, when, snoozed }: { item: DayItem; section: Section; index: number; ctx: DayCtx; when: string; snoozed: boolean }) {
  const { ctrl } = ctx
  const { phase, mark } = ctrl.phaseOf(item.key, section)
  const menu = ctrl.menu?.key === item.key ? ctrl.menu : null
  const mail = item.mail
  return (
    <div className="day-row" data-phase={phase} data-mark={mark} role="listitem">
      <div className="day-clip">
        <div
          className="day-prow flex items-center gap-3 border-t border-line-3 py-[9px] pl-10 pr-4 text-[12px]"
          tabIndex={0}
          data-day-nav=""
          data-day-key={item.key}
          data-day-section={section}
          data-day-index={index}
          data-open={menu ? 'true' : undefined}
        >
          <span className="w-[150px] flex-none truncate text-ink-3">{mail ? senderShort(mail) : item.title}</span>
          <a href={item.url ?? undefined} target="_blank" rel="noreferrer" tabIndex={-1} className="day-strike min-w-0 flex-1 truncate text-ink-2 no-underline hover:text-ink">
            {item.title}
          </a>
          <span className="day-prow-when flex-none text-[11px] text-microcopy">{when}</span>
          <div className="day-acts flex-none items-center gap-1">
            <IconAct label="Done" k="D" tip="You dealt with it. It leaves the list; undo for 5 seconds." onClick={() => ctrl.finish(item, section, index, 'done')}>
              <CheckGlyph size={13} className="text-success" />
            </IconAct>
            {!snoozed && mail && (
              <span className="relative">
                <IconAct
                  label="Snooze"
                  k="S"
                  tip="Hide it until tonight, tomorrow morning, next week or a date you pick. It waits in Snoozed."
                  onClick={() => ctrl.setMenu(menu ? null : { key: item.key, kind: 'snooze' })}
                >
                  <MoonGlyph size={13} />
                </IconAct>
                <SnoozeMenu
                  open={menu?.kind === 'snooze' || menu?.kind === 'picker'}
                  picker={menu?.kind === 'picker'}
                  today={ctx.today}
                  nowMinutes={ctx.nowMinutes}
                  target={{ kind: 'mail', receivedAt: mail.receivedAt }}
                  pick={ctx.pick}
                  onPick={ctx.setPick}
                  onPreset={(c) => ctrl.snooze(item, section, index, c.until, presetPhrase(c))}
                  onPicked={(until) => ctrl.snooze(item, section, index, until, pickedPhrase(until))}
                  onOpenPicker={() => ctrl.setMenu({ key: item.key, kind: 'picker' })}
                  onClose={() => ctrl.setMenu(null)}
                  align="right"
                />
              </span>
            )}
            <IconAct label="Dismiss" k="X" tip="Not relevant. It leaves for good; undo for 5 seconds." onClick={() => ctrl.finish(item, section, index, 'dismiss')}>
              <XGlyph size={13} />
            </IconAct>
            {!snoozed && mail && <PileSegs verdict={mail.verdict} item={mail} onMove={(v) => ctrl.move(item, section, index, v)} />}
          </div>
        </div>
      </div>
    </div>
  )
}

function IconAct({ label, k, tip, onClick, children }: { label: string; k: string; tip: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="day-btn day-tipw h-[26px] w-[26px] justify-center p-0">
      {children}
      <Tip name={label} k={k} align="right">
        {tip}
      </Tip>
    </button>
  )
}
